// ReportsPage — страница «Отчёты» (V14 W9, ТЗ §3.6; канон PaymentsListPage).
// Селектор отчёта (Tabs, статический каталог REPORTS — Q1), пикер периода (скрыт для
// as-of отчётов funnel/risks), деп-фильтр (только admin — §3.5), таблица результата,
// кнопка «Выгрузить XLSX», состояния loading/empty/error и предупреждение об усечении
// (meta.truncated → B2-контракт). Данные: GET /api/reports/{id} (TanStack Query,
// queryKey ['reports', id, from, to, departmentId, inactiveDays]).

import { useEffect, useMemo, useState } from 'react';
import { subMonths, startOfDay, endOfDay } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Stack, Tab, Tabs, TextField } from '@mui/material';
import { useAuth } from '@/auth/AuthContext';
import { reportsApi, REPORTS, type ReportDescriptor, type ReportId, type ReportParams } from '@/api/reports';
import { extractApiErrorMessage } from '@/lib/apiError';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { t } from '@/i18n';
import { ReportTable } from './components/ReportTable';
import { ReportPeriodPicker, type ReportPeriodValue } from './components/ReportPeriodPicker';
import { ReportDepartmentFilter } from './components/ReportDepartmentFilter';
import { ReportDownloadButton } from './components/ReportDownloadButton';

/** Дефолт порога без активности R6 (Q7: пикер 1..90, дефолт 7). */
const DEFAULT_INACTIVE_DAYS = 7;

/** Границы порога без активности R6 (BE-контракт ReportQuery: 1..90, иначе 400). */
const INACTIVE_DAYS_MIN = 1;
const INACTIVE_DAYS_MAX = 90;

/** Резолвит дескриптор отчёта по id (TS-strict: find → возможно undefined; id всегда из Tabs). */
function resolveReport(id: ReportId): ReportDescriptor {
  const found = REPORTS.find((item) => item.id === id);
  if (found) return found;
  throw new Error(`Unknown report id: ${id}`);
}

/** Преобразует локальные даты пикера в ISO-границы дня (startOfDay/endOfDay, канон FIX-C). */
function toIsoRange(value: ReportPeriodValue): ReportParams {
  const params: ReportParams = {};
  if (value.from) params.from = startOfDay(value.from).toISOString();
  if (value.to) params.to = endOfDay(value.to).toISOString();
  return params;
}

/** Страница «Отчёты»: выбор отчёта → период/деп-фильтр → таблица + XLSX. */
export function ReportsPage(): JSX.Element {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.roles.some((role) => role.code === 'admin') ?? false;

  const [reportId, setReportId] = useState<ReportId>('funnel');
  const report = resolveReport(reportId);
  const [period, setPeriod] = useState<ReportPeriodValue>({ from: null, to: null });
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [inactiveDays, setInactiveDays] = useState<number>(DEFAULT_INACTIVE_DAYS);
  // Текстовое состояние поля (коммит onBlur/Enter: контролируемый Number-инпут ломает набор «30»).
  const [inactiveDaysText, setInactiveDaysText] = useState<string>(String(DEFAULT_INACTIVE_DAYS));

  const commitInactiveDays = (raw: string): void => {
    const value = Number.parseInt(raw, 10);
    const clamped = Number.isFinite(value)
      ? Math.min(INACTIVE_DAYS_MAX, Math.max(INACTIVE_DAYS_MIN, value))
      : DEFAULT_INACTIVE_DAYS;
    setInactiveDays(clamped);
    setInactiveDaysText(String(clamped));
  };

  // I5-фикс: дефолт monthly-dynamics — from = now−12 мес, to = now (иначе пикер пуст).
  useEffect(() => {
    if (report.id === 'monthly-dynamics' && period.from === null && period.to === null) {
      setPeriod({ from: subMonths(new Date(), 12), to: new Date() });
    }
  }, [report.id, period.from, period.to]);

  const params = useMemo<ReportParams>(() => {
    const range = report.usePeriod ? toIsoRange(period) : {};
    return {
      ...range,
      ...(report.useDepartment && isAdmin && departmentId ? { departmentId } : {}),
      ...(report.useInactiveDays ? { inactiveDays } : {}),
    };
  }, [report, isAdmin, departmentId, period, inactiveDays]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', report.id, params.from ?? null, params.to ?? null, params.departmentId ?? null, params.inactiveDays ?? null],
    queryFn: () => reportsApi.getReport(report.id, params),
  });

  return (
    <Box>
      <PageHeader title={t('reports.title')} subtitle={t('reports.subtitle')} />

      <Tabs
        value={report.id}
        onChange={(_, value: ReportId) => setReportId(value)}
        variant="scrollable"
        scrollButtons="auto"
        data-testid="report-tabs"
        sx={{ mb: 2 }}
      >
        {REPORTS.map((item) => (
          <Tab
            key={item.id}
            value={item.id}
            label={t(item.titleKey)}
            data-testid={`report-tab-${item.id}`}
          />
        ))}
      </Tabs>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" useFlexGap sx={{ mb: 2 }}>
        {report.usePeriod ? <ReportPeriodPicker value={period} onChange={setPeriod} /> : null}
        {report.useDepartment && isAdmin ? (
          <ReportDepartmentFilter value={departmentId} onChange={setDepartmentId} />
        ) : null}
        {report.useInactiveDays ? (
          <TextField
            type="number"
            size="small"
            label={t('reports.inactiveDays')}
            value={inactiveDaysText}
            inputProps={{ min: INACTIVE_DAYS_MIN, max: INACTIVE_DAYS_MAX, 'aria-label': t('reports.inactiveDays') }}
            data-testid="report-inactive-days"
            onChange={(event) => setInactiveDaysText(event.target.value)}
            onBlur={(event) => commitInactiveDays(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                commitInactiveDays((event.target as HTMLInputElement).value);
              }
            }}
          />
        ) : null}
        <ReportDownloadButton reportId={report.id} params={params} disabled={isLoading} />
      </Stack>

      {isLoading ? (
        <Box data-testid="reports-loading">
          <LoadingSpinner label={t('reports.loading')} />
        </Box>
      ) : isError || !data ? (
        <Alert severity="error" data-testid="reports-error">
          {extractApiErrorMessage(error, t('reports.loadError'))}
        </Alert>
      ) : (
        <>
          {data.meta.truncated ? (
            <Alert severity="warning" data-testid="reports-truncated-warning" sx={{ mb: 2 }}>
              {t('reports.truncated')}
            </Alert>
          ) : null}
          {data.rows.length === 0 ? (
            <Box data-testid="reports-empty">
              <EmptyState title={t('reports.empty')} description={t('reports.emptyDesc')} />
            </Box>
          ) : (
            <ReportTable report={report} response={data} />
          )}
        </>
      )}
    </Box>
  );
}
